import { Component, OnInit } from '@angular/core';
import { firstValueFrom } from 'rxjs';

import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { WindowService } from 'src/shared/providers/window.service';
import { TidyStorage } from 'src/shared/providers/tidy-storage';

import { CustomerGuestsApiService } from 'src/shared/services/customer-guests-api.service';
import { Util } from 'src/shared/util/util';

import type { GuestPickerValue } from 'src/shared/components/guest-picker/guest-picker.component';
import type { CustomerGuest } from 'src/models/customer-guest.model';

import { MessageThreadPage } from '../messages/message-thread/message-thread';

@Component({
    templateUrl: 'new-message.html',
    standalone: false
})

export class NewMessagePage implements OnInit {

  proList: any;
  pros: any;
  mode: 'pros' | 'guest' = 'pros';
  selectedGuestValue: GuestPickerValue | null = null;
  guestSubmitted = false;
  dialogParams: any;

  boundMessageGuest = this.messageGuest.bind(this);

  constructor(
    private navCtrl: CustomNavController,
    private rightSidePanelService: RightSidePanelService,
    private storage: TidyStorage,
    public windowService: WindowService,
    private guestsApi: CustomerGuestsApiService,
    private util: Util,
  ) {}

  async ngOnInit() {
    this.rightSidePanelService.currentComponentData = null;
    this.dialogParams = await this.storage.retrieve('dialog-params');
    const messageMode = this.dialogParams?.messageMode ?? this.navCtrl.getParam('messageMode');
    this.mode = messageMode === 'guest' ? 'guest' : 'pros';
    this.setPageTitle();
    this.proList = await this.storage.retrieve('proList');
    this.pros = await this.storage.retrieve('pros');
  }

  switchToGuestMode(): void {
    this.mode = 'guest';
    this.setPageTitle();
  }

  switchToProsMode(): void {
    this.mode = 'pros';
    this.guestSubmitted = false;
    this.setPageTitle();
  }

  private setPageTitle(): void {
    this.rightSidePanelService.setDialogPageTitle(this.mode === 'guest' ? 'Message a Guest' : 'New Message');
  }

  onGuestChange(value: GuestPickerValue | null): void {
    this.selectedGuestValue = value;
  }

  async messageGuest(): Promise<void> {
    this.guestSubmitted = true;
    const value = this.selectedGuestValue;
    if (!value) {
      return;
    }
    const loading = await this.util.showLoading();
    try {
      const guest = value.mode === 'existing'
        ? value.guest
        : await firstValueFrom(this.guestsApi.createGuest(value.payload));
      loading.dismiss();
      await this.goToGuestThread(guest);
    } catch (error) {
      loading.dismiss();
      this.util.showError(error?.error?.message || error?.message || 'Could not start conversation');
    }
  }

  private async goToGuestThread(guest: CustomerGuest): Promise<void> {
    const params: any = {
      backPage: 'messages',
      proList: this.proList,
      pros: this.pros,
      guestId: guest.id,
      pageTitle: guest.name || 'Guest',
      guestPhone: guest.phone,
    };
    if (guest.chat_room_key) {
      params.chatRoomKey = guest.chat_room_key;
    }
    if (this.windowService.isDesktopRes) {
      await this.storage.save('newMessageGuest', guest);
      await this.rightSidePanelService.closeRightSidePanel();
    } else {
      this.navCtrl.navigateForward('message-thread', params);
    }
  }

  async goToMessageThread(pro: any) {
    const params: any = {
      backPage: 'messages',
      proList: this.proList,
      pros: this.pros,
    };
    if (pro.chatRoomKey) {
      params.chatRoomKey = pro.chatRoomKey;
      params.pageTitle = pro.viewValue;
    } else {
      const proxyPro = this.findProxyProForTeam(pro?.teamId);
      if (proxyPro) {
        params.chatRoomKey = proxyPro.chat_room_key;
        params.pageTitle = `${proxyPro.first_name ?? ''} ${proxyPro.last_name ?? ''}`.trim();
      } else {
        params.proId = pro.proId;
      }
    }
    if (this.windowService.isDesktopRes) {
      this.rightSidePanelService.navigateTo('message-thread', params, MessageThreadPage);
    } else {
      this.navCtrl.navigateForward('message-thread', params);
    }
  }

  private findProxyProForTeam(teamId: number): any {
    if (!teamId) {
      return null;
    }
    return this.pros?.find(
      (p: any) => p?.chat_room_key
        && p?.data?.number_type === 'homekeeper'
        && (p?.data?.team_id === teamId || (typeof p.chat_room_key === 'string' && p.chat_room_key.includes(`team_id-${teamId}`)))
    ) || null;
  }

}
